import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Inner, fadeInUp } from '../styles/v2.styled';
import { checkSession, ssoLogin, ssoLogout } from '../../Common/Utils/SsoSession';

const Bar = styled.section`
  background: #073f48;
  padding: 28px 0;

  @media (max-width: 640px) {
    padding: 22px 0;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  animation: ${fadeInUp} 0.5s ease both;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Left = styled.div`
  display: grid;
  grid-template-columns: 4px 1fr;
  gap: 16px;
  align-items: center;

  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 48px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.58);
  }
`;

const TextGroup = styled.div``;

const Welcome = styled.div`
  font-size: 1.05rem;
  font-weight: 900;
  color: #fff;
  margin-bottom: 4px;
`;

const SubText = styled.div`
  font-size: 0.86rem;
  color: rgba(255, 255, 255, 0.76);
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FormRow = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Input = styled.input`
  width: 160px;
  min-height: 42px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 0.9rem;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.62);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.62);
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const Btn = styled.button`
  min-height: 42px;
  padding: 0 18px;
  border-radius: 8px;
  border: 0;
  background: #fff;
  color: #005c68;
  font-size: 0.9rem;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
`;

const SecBtn = styled(Link)`
  min-height: 42px;
  padding: 0 18px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  background: transparent;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 800;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const HelperLinks = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 10px;
  justify-content: flex-end;

  a {
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.82rem;
    text-decoration: none;
  }

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

function LoginSection() {
  const [session, setSession] = useState(null);
  const [loginData, setLoginData] = useState({ UserId: '', UserPw: '' });

  useEffect(() => {
    checkSession()
      .then((s) => {
        if (s && s.State === 'LOGIN') setSession(s);
      })
      .catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.UserId) { alert('아이디를 입력하세요.'); return; }
    if (!loginData.UserPw) { alert('비밀번호를 입력하세요.'); return; }

    try {
      const r = await ssoLogin(loginData.UserId, loginData.UserPw);
      if (r && r.success) {
        // .antsnest.co.kr 통합 세션 쿠키 발급 → 모든 SSO 서비스에서 동시 로그인
        window.location.reload();
      } else {
        alert(r?.message || '아이디 또는 비밀번호가 올바르지 않습니다.');
      }
    } catch (err) {
      alert('로그인 중 오류가 발생했습니다.');
    }
  };

  const handleLogout = async () => {
    await ssoLogout();
    window.location.reload();
  };

  if (session && session.State === 'LOGIN') {
    return (
      <Bar>
        <Inner>
          <Content>
            <Left>
              <TextGroup>
                <Welcome>{session.Name || session.user_name || '회원'}님, 환영합니다.</Welcome>
                <SubText>내 강의실에서 수강 중인 과정을 이어서 학습하세요.</SubText>
              </TextGroup>
            </Left>
            <Actions>
              <SecBtn to="/MyClassroom">내 강의실</SecBtn>
              <Btn onClick={handleLogout} type="button">로그아웃</Btn>
            </Actions>
          </Content>
        </Inner>
      </Bar>
    );
  }

  return (
    <Bar>
      <Inner>
        <Content>
          <Left>
            <TextGroup>
              <Welcome>로그인하고 학습을 이어가세요</Welcome>
              <SubText>수강신청, 강의실, 커뮤니티 기능을 한 곳에서 이용할 수 있습니다.</SubText>
            </TextGroup>
          </Left>
          <div>
            <FormRow onSubmit={handleLogin}>
              <Input
                type="text"
                placeholder="아이디"
                value={loginData.UserId}
                onChange={(e) => setLoginData({ ...loginData, UserId: e.target.value })}
              />
              <Input
                type="password"
                placeholder="비밀번호"
                value={loginData.UserPw}
                onChange={(e) => setLoginData({ ...loginData, UserPw: e.target.value })}
              />
              <Btn type="submit">로그인</Btn>
            </FormRow>
            <HelperLinks>
              <Link to="/MemberJoin">회원가입</Link>
              <Link to="/FindMemberInfo">ID/PW 찾기</Link>
            </HelperLinks>
          </div>
        </Content>
      </Inner>
    </Bar>
  );
}

export default LoginSection;
